import { useState } from 'react';

import { isApiError } from '@platform/api-client';
import type { AdminIntegration } from '@platform/contracts';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  SchemaForm,
  schemaInitialValues,
  schemaIssuesToFieldErrors,
  schemaSubmittedValues,
  schemaVisibleValues,
  type SchemaValues,
  useToast,
  validateSchemaValues,
} from '@platform/ui';

import { useIntegrationsClient } from '../../lib/api/integrations-client-context';

/**
 * The Settings tab (MASTER_PLAN M3.9, Docs/API.md §2.10).
 *
 * Non-secret configuration only: practice ids, sending domains, a model name. The form is
 * generated from the catalog's `settingsSchema` and submitted with `PATCH .../settings`,
 * which the API validates against the same schema and which invalidates the cached
 * adapter, so a change takes effect on the next vendor call rather than the next restart.
 *
 * Not step-up. Changing a sending domain is reversible and visible; changing a credential
 * is neither, which is why only that one asks for a second factor.
 */

export interface SettingsFormProps {
  entry: AdminIntegration;
  onChanged: () => void;
}

export function SettingsForm({ entry, onChanged }: SettingsFormProps) {
  const client = useIntegrationsClient();
  const { toast } = useToast();

  const schema = entry.integration.settingsSchema;
  const install = entry.install;

  const [values, setValues] = useState<SchemaValues>(() =>
    schemaInitialValues(schema, install?.settings ?? {}),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  if (install === null) {
    return (
      <Alert tone="info" title="This integration is not enabled for your clinic.">
        Ask us to turn it on and its settings appear here.
      </Alert>
    );
  }

  const save = async (): Promise<void> => {
    const local = validateSchemaValues(schema, values);
    setErrors(local);
    setFormError(undefined);
    if (Object.keys(local).length > 0) return;

    setBusy(true);
    try {
      await client.patchSettings(
        entry.integration.key,
        schemaSubmittedValues(schemaVisibleValues(schema, values)),
      );
      toast({ tone: 'success', message: 'Settings saved.' });
      onChanged();
    } catch (error) {
      if (isApiError(error) && error.code === 'VALIDATION_FAILED') {
        const mapped = schemaIssuesToFieldErrors(schema, error.issues);
        const { '': formLevel, ...fields } = mapped;
        setErrors(fields);
        setFormError(formLevel ?? error.message);
      } else {
        setFormError(isApiError(error) ? error.message : 'That could not be saved. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader
        as="h2"
        title="Settings"
        subtitle="How this clinic uses the integration. Nothing here is secret."
      />
      <form
        className="px-6 pt-5 pb-6 max-[480px]:px-4"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        {formError === undefined ? null : (
          <Alert tone="error" className="mb-5">
            {formError}
          </Alert>
        )}

        <SchemaForm
          schema={schema}
          values={values}
          onChange={(name, value) => {
            setValues((current) => ({ ...current, [name]: value }));
            setErrors((current) => {
              const { [name]: _removed, ...rest } = current;
              return rest;
            });
          }}
          errors={errors}
          disabled={busy}
          idPrefix="settings"
        />

        <div className="mt-5">
          {/* `type="button"` with an explicit handler, not a submit button: the form's own
              `onSubmit` still handles Enter inside a field, and a real browser does not
              double-fire because a non-submit button never submits. */}
          <Button
            disabled={busy}
            onClick={() => {
              void save();
            }}
          >
            {busy ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
